// Complete PDPConfiguration with Independent Cart Handling
class PDPConfiguration extends HTMLElement {
    constructor() {
        super();
  
        this.presetQuantities = this.querySelectorAll('input[name="preset-qty"]');
        this.attributeQuantity = this.querySelectorAll('.quantity__input');
        this.lensesOption = this.querySelectorAll('.lens_options');
        this.enableAttribute = this.querySelectorAll('.enable_attribute');
        this.quantityInput = document.querySelector('input.quantity__input[name="quantity"]');
        this.isPromo = this.getAttribute('data-is-promo') === 'true';
        this.isLensProduct = false;
    }

    connectedCallback() {
        // Initialize when element is connected to DOM
        this.init();
    }

    init() {
        // Check if this is a lens product (has eye checkboxes)
        const rechtsCheckbox = this.querySelector('#enable_rechts');
        const linksCheckbox = this.querySelector('#enable_links');
        this.isLensProduct = !!(rechtsCheckbox || linksCheckbox);

        // Set defaults after element is in DOM
        this.setAllLensDefaults();

        // Add event listeners
        this.presetQuantities.forEach(radio => {
            radio.addEventListener('change', () => this.radioChanged(this, true));
        });

        // Update LensesOption Attribute
        this.lensesOption.forEach(select => {
            this.updatelensesAttribute(select);
            select.addEventListener("change", () => this.updatelensesAttribute(select));
        });

        // Enable Lenses Attribute
        this.enableAttribute.forEach(input => {
            this.enableLensesAttribute(input);
            input.addEventListener("change", () => this.enableLensesAttribute(input));
        });

        // Quantity input change handlers
        this.attributeQuantity.forEach(input => {
            input.addEventListener('change', () => this.quantityChange(input, false));
            input.addEventListener('input', () => this.quantityChange(input, false));
        });

        // Fire once on init
        this.radioChanged(this, true);
        this.rePositionElements();
        
        // Ensure lens selects are always enabled in promo mode
        if (this.isPromo) {
            this.ensureLensSelectsEnabled();
        }

        // Setup custom form handling for lens products
        if (this.isLensProduct) {
            this.setupCustomFormHandling();
        }
    }

    setupCustomFormHandling() {
        // Find the original submit button
        const productForm = document.querySelector('product-form');
        const originalSubmitButton = productForm?.querySelector('button[type="submit"]');
        
        if (originalSubmitButton && this.isLensProduct) {
            // Hide the original submit button
            originalSubmitButton.style.display = 'none';
            
            // Create our custom submit button
            const customButton = originalSubmitButton.cloneNode(true);
            customButton.style.display = '';
            customButton.type = 'button'; // Change from submit to button
            customButton.removeAttribute('form'); // Remove form association
            
            // Insert our custom button after the hidden one
            originalSubmitButton.parentNode.insertBefore(customButton, originalSubmitButton.nextSibling);
            
            // Add our click handler
            customButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleCustomSubmit();
            });
            
            // Store references
            this.customSubmitButton = customButton;
            this.originalProductForm = productForm;
        }
    }

    async handleCustomSubmit() {
        // Get form data
        const form = this.originalProductForm?.querySelector('form');
        if (!form) {
            console.error('Form not found');
            return;
        }

        // Get checkboxes and quantities
        const rechtsCheckbox = this.querySelector('#enable_rechts');
        const linksCheckbox = this.querySelector('#enable_links');
        const rechtsInput = this.querySelector('input[name="rechts_qty"]');
        const linksInput = this.querySelector('input[name="links_qty"]');
        
        const rechtsQty = parseInt(rechtsInput?.value || 0);
        const linksQty = parseInt(linksInput?.value || 0);
        
        // Validate lens selects for enabled eyes
        const validationError = this.validateLensSelects(rechtsCheckbox?.checked && rechtsQty > 0, linksCheckbox?.checked && linksQty > 0);
        if (validationError) {
            this.showError(validationError);
            return;
        }
        
        // Get the variant ID
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (!variantId) {
            this.showError('Product variant not found');
            return;
        }
        
        // Prepare items to add
        const itemsToAdd = [];
        
        // Build rechts item if enabled AND quantity > 0
        if (rechtsCheckbox?.checked && rechtsQty > 0) {
            const rechtsItem = {
                id: variantId,
                quantity: rechtsQty,
                properties: {
                    'Eye': 'Rechts'
                }
            };
            
            // Add lens attributes
            this.addLensAttributesToItem(rechtsItem, 'rechts', form);
            itemsToAdd.push(rechtsItem);
        }
        
        // Build links item if enabled AND quantity > 0
        if (linksCheckbox?.checked && linksQty > 0) {
            const linksItem = {
                id: variantId,
                quantity: linksQty,
                properties: {
                    'Eye': 'Links'
                }
            };
            
            // Add lens attributes
            this.addLensAttributesToItem(linksItem, 'links', form);
            itemsToAdd.push(linksItem);
        }
        
        // Validate
        if (itemsToAdd.length === 0) {
            this.showError('Please select at least one eye and set a quantity.');
            return;
        }

        // Show loading state
        this.setLoadingState(true);

        try {
            // Get cart drawer/notification element
            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            
            // Build request body
            const requestBody = {
                items: itemsToAdd
            };

            // Add sections for cart drawer update
            if (cart) {
                requestBody.sections = cart.getSectionsToRender().map((section) => section.id);
                requestBody.sections_url = window.location.pathname;
                if (cart.setActiveElement) {
                    cart.setActiveElement(document.activeElement);
                }
            }

            console.log('Sending custom request:', requestBody);

            // Make the request
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(requestBody)
            });

            const responseData = await response.json();
            console.log('Response:', responseData);

            if (response.ok) {
                // Success!
                this.handleSuccessfulAdd(responseData, cart);
            } else {
                // Error from Shopify
                this.showError(responseData.message || responseData.description || 'Error adding to cart');
            }
        } catch (error) {
            console.error('Network error:', error);
            this.showError('Network error. Please check your connection and try again.');
        } finally {
            this.setLoadingState(false);
        }
    }

    handleSuccessfulAdd(responseData, cart) {
        // Clear any errors
        this.clearErrors();

        // If no cart drawer/notification, redirect to cart
        if (!cart) {
            window.location.href = window.Shopify.routes.root + 'cart';
            return;
        }

        // Trigger cart update events for theme compatibility
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                cartData: responseData,
            });
        }

        // Dispatch custom event for other scripts
        document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { 
                cart: responseData,
                source: 'pdp-configuration'
            }
        }));

        // Handle quick add modal
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
            document.body.addEventListener(
                'modalClosed',
                () => {
                    setTimeout(() => {
                        this.renderCartUpdate(cart, responseData);
                    });
                },
                { once: true }
            );
            quickAddModal.hide(true);
        } else {
            // Render cart update
            this.renderCartUpdate(cart, responseData);
        }

        // Remove empty cart class if present
        if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
        }

        // Open cart drawer if it exists
        if (cart.classList.contains('drawer') || cart.tagName === 'CART-DRAWER') {
            this.openCartDrawer(cart);
        }
    }

    renderCartUpdate(cart, responseData) {
        // Use the cart's render method if available
        if (cart.renderContents) {
            cart.renderContents(responseData);
        } else if (cart.getSectionsToRender) {
            // Manual update if renderContents isn't available
            const sections = cart.getSectionsToRender();
            sections.forEach((section) => {
                const sectionElement = document.querySelector(section.selector) || document.getElementById(section.id);
                if (sectionElement && responseData.sections && responseData.sections[section.id]) {
                    sectionElement.innerHTML = responseData.sections[section.id];
                }
            });
        }
    }

    openCartDrawer(cart) {
        // Try different methods to open the cart drawer
        if (cart.open) {
            cart.open();
        } else if (cart.classList.contains('drawer')) {
            // Manual drawer opening
            cart.classList.add('animate', 'active');
            
            // Also try to find and add overlay
            const overlay = document.querySelector('.cart-drawer-overlay') || 
                           document.querySelector('[id*="cart-drawer-overlay"]');
            if (overlay) {
                overlay.classList.add('active');
            }
            
            // Prevent body scroll
            document.body.classList.add('overflow-hidden');
            
            // Dispatch event
            cart.dispatchEvent(new CustomEvent('cart-drawer:opened'));
        }
    }

    addLensAttributesToItem(item, eyeType, form) {
        // Map eye type for form fields
        const prefix = eyeType === 'rechts' ? 'R' : 'L';
        
        console.log(`Getting attributes for ${eyeType} (${prefix})`);
        
        // Method 1: Get values from the selects in the table
        const eyeSelects = document.querySelectorAll(`.${eyeType} select.lens_options`);
        eyeSelects.forEach(select => {
            const targetId = select.getAttribute('data-target');
            const value = select.value;
            
            if (targetId && value) {
                // Extract the property name from the target ID
                // R-sterkte -> R-Sterkte, L-dia -> L-Dia, etc.
                const parts = targetId.split('-');
                if (parts.length >= 2) {
                    const propertyName = parts[0] + '-' + parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
                    item.properties[propertyName] = value;
                    console.log(`Added ${propertyName}: ${value}`);
                }
            }
        });
        
        // Method 2: Also check the hidden/text inputs in append-to-form as fallback
        if (eyeType === 'rechts') {
            // For Rechts, check the direct property inputs
            const sterkte = document.querySelector('#R-sterkte')?.value;
            const dia = document.querySelector('#R-dia')?.value;
            const bc = document.querySelector('#R-BC')?.value;
            
            // Only add if not already set from selects
            if (sterkte && !item.properties['R-Sterkte']) {
                item.properties['R-Sterkte'] = sterkte;
            }
            if (dia && !item.properties['R-Dia']) {
                item.properties['R-Dia'] = dia;
            }
            if (bc && !item.properties['R-BC']) {
                item.properties['R-BC'] = bc;
            }
        } else if (eyeType === 'links') {
            // For Links, check both naming patterns
            const sterkte = document.querySelector('input[name="items[0][properties][L-Sterkte]"]')?.value || 
                           document.querySelector('#L-sterkte')?.value;
            const dia = document.querySelector('input[name="items[0][properties][L-Dia]"]')?.value || 
                       document.querySelector('#L-dia')?.value;
            const bc = document.querySelector('input[name="items[0][properties][L-BC]"]')?.value || 
                      document.querySelector('#L-BC')?.value;
            
            // Only add if not already set from selects
            if (sterkte && !item.properties['L-Sterkte']) {
                item.properties['L-Sterkte'] = sterkte;
            }
            if (dia && !item.properties['L-Dia']) {
                item.properties['L-Dia'] = dia;
            }
            if (bc && !item.properties['L-BC']) {
                item.properties['L-BC'] = bc;
            }
        }
        
        // Method 3: Check for any additional lens option selects (for other attributes)
        const additionalSelects = this.querySelectorAll(`.${eyeType} select:not([data-target*="sterkte"]):not([data-target*="dia"]):not([data-target*="bc"])`);
        additionalSelects.forEach(select => {
            if (select.value && select.getAttribute('data-target')) {
                const targetId = select.getAttribute('data-target');
                const label = select.closest('tr')?.querySelector('td:first-child')?.textContent?.trim() || 
                             targetId.replace(prefix + '-', '');
                const propertyKey = `${prefix}-${label}`;
                if (!item.properties[propertyKey]) {
                    item.properties[propertyKey] = select.value;
                    console.log(`Added additional property ${propertyKey}: ${select.value}`);
                }
            }
        });
        
        console.log('Final properties for', eyeType, ':', item.properties);
    }

    setLoadingState(loading) {
        if (!this.customSubmitButton) return;
        
        const spinner = this.customSubmitButton.querySelector('.loading__spinner');
        const buttonText = this.customSubmitButton.querySelector('span');
        
        if (loading) {
            this.customSubmitButton.setAttribute('aria-disabled', 'true');
            this.customSubmitButton.classList.add('loading');
            if (spinner) spinner.classList.remove('hidden');
            if (buttonText) {
                this.originalButtonText = buttonText.textContent;
                buttonText.textContent = 'Adding to cart...';
            }
        } else {
            this.customSubmitButton.removeAttribute('aria-disabled');
            this.customSubmitButton.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            if (buttonText && this.originalButtonText) {
                buttonText.textContent = this.originalButtonText;
            }
        }
    }

    showError(message) {
        // Try to use the theme's error display
        const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
        const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
        
        if (errorWrapper && errorMessage) {
            errorMessage.textContent = message;
            errorWrapper.hidden = false;
            
            // Auto-hide after 5 seconds
            setTimeout(() => {
                errorWrapper.hidden = true;
            }, 5000);
        } else {
            // Fallback to alert
            alert(message);
        }
    }

    clearErrors() {
        const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
        if (errorWrapper) {
            errorWrapper.hidden = true;
        }
    }

    validateLensSelects(rechtsEnabled, linksEnabled) {
        // Only validate if an eye is enabled (checkbox checked AND quantity > 0)
        if (rechtsEnabled) {
            const rechtsSelects = this.querySelectorAll('.rechts select.lens_options');
            for (const select of rechtsSelects) {
                if (!select.value || select.value.trim() === '') {
                    return 'Please select all lens options for Rechts (Right) eye before adding to cart.';
                }
            }
        }
        
        if (linksEnabled) {
            const linksSelects = this.querySelectorAll('.links select.lens_options');
            for (const select of linksSelects) {
                if (!select.value || select.value.trim() === '') {
                    return 'Please select all lens options for Links (Left) eye before adding to cart.';
                }
            }
        }
        
        return null; // No validation errors
    }

    rePositionElements() {
        // Clean up the form
        let productForm = document.querySelector("product-form form");
        let appendToFormDiv = this.querySelector(".append-to-form");

        if (productForm && appendToFormDiv) {
            // Hide the append-to-form div
            appendToFormDiv.style.display = 'none';
            
            // For lens products, disable the standard quantity input
            if (this.isLensProduct) {
                const standardQtyInput = productForm.querySelector('input[name="quantity"]');
                if (standardQtyInput) {
                    standardQtyInput.removeAttribute('name');
                    standardQtyInput.setAttribute('data-original-name', 'quantity');
                    standardQtyInput.disabled = true;
                }
            }
        }
    }

    // ... (keep all the other existing methods below)
    
    setAllLensDefaults() {
        // Update all lens option selects and their target inputs
        this.lensesOption.forEach(select => {
            this.updatelensesAttribute(select);
        });
        
        // Also ensure the inputs in append-to-form have the default values
        const allSelects = document.querySelectorAll('select.lens_options[data-target]');
        allSelects.forEach(select => {
            const targetId = select.getAttribute('data-target');
            const targetInput = document.getElementById(targetId);
            if (targetInput && select.value) {
                targetInput.value = select.value;
                console.log(`Set default for ${targetId}: ${select.value}`);
            }
        });
        
        // Set quantity defaults
        const rechtsInput = this.querySelector('input[name="rechts_qty"]');
        const linksInput = this.querySelector('input[name="links_qty"]');
        if (rechtsInput) this.quantityChange(rechtsInput, false);
        if (linksInput) this.quantityChange(linksInput, false);
    }

    radioChanged(element, isRadio) {
        const checked = element.querySelector('input[name="preset-qty"]:checked');
        if (!checked) return;

        const value = checked.value;
        const price_update = checked.getAttribute('data-discount-price');
        const priceID = element.getAttribute('data-price');
        const priceElement = document.getElementById(priceID);
        const priceItem = priceElement?.querySelector('.price-item--regular');
        
        if (priceItem) {
            priceItem.innerHTML = price_update;
        }

        this.attributeQuantity.forEach(input => {
            input.value = value;
            const event = new Event('change', { bubbles: true });
            input.dispatchEvent(event);
        });

        if (window.PriceCalculator) {
            window.PriceCalculator.updateTotalPrice();
        }
    }

    quantityChange(element, isRadio) {
        if (!element) return;

        const rechtsInput = this.querySelector('input[name="rechts_qty"]');
        const linksInput = this.querySelector('input[name="links_qty"]');

        const rechtsQty = parseInt(rechtsInput?.value || 0);
        const linksQty = parseInt(linksInput?.value || 0);
        const totalQty = rechtsQty + linksQty;

        console.log(`Quantity change - Rechts: ${rechtsQty}, Links: ${linksQty}, Total: ${totalQty}`);

        if (window.PriceCalculator) {
            window.PriceCalculator.updateTotalPrice();
        }
    }

    updatelensesAttribute(select) {
        let targetId = select.getAttribute("data-target");
        let targetInput = document.getElementById(targetId);
        
        if (targetInput) {
            targetInput.value = select.value;
            console.log(`Updated ${targetId} to ${select.value}`);
            
            // Also trigger change event in case other scripts are listening
            const event = new Event('change', { bubbles: true });
            targetInput.dispatchEvent(event);
        }
        
        // Also check if this select is within a specific eye section
        const eyeSection = select.closest('.rechts, .links');
        if (eyeSection) {
            const isRechts = eyeSection.classList.contains('rechts');
            const prefix = isRechts ? 'R' : 'L';
            
            // Try to find the corresponding input in the append-to-form section
            const label = select.closest('.lens-option-wrapper')?.querySelector('label')?.textContent || 
                         select.getAttribute('data-label') || 
                         select.name;
            
            if (label) {
                // Clean up the label to match input IDs
                const cleanLabel = label.trim().toLowerCase()
                    .replace(/[:\s]+/g, '-')
                    .replace(/sterkte/i, 'sterkte')
                    .replace(/dia/i, 'dia')
                    .replace(/bc/i, 'bc');
                
                // Try different ID patterns
                const possibleIds = [
                    `${prefix}-${cleanLabel}`,
                    `${prefix}${cleanLabel}`,
                    `${prefix}_${cleanLabel}`
                ];
                
                for (const id of possibleIds) {
                    const input = document.getElementById(id);
                    if (input && input !== targetInput) {
                        input.value = select.value;
                        console.log(`Also updated ${id} to ${select.value}`);
                        break;
                    }
                }
            }
        }
    }

    enableLensesAttribute(input) {
        const retchsItems = document.querySelectorAll('.rechts');
        const linksItems = document.querySelectorAll('.links');
        
        if (input.checked) {
            this.radioChanged(this, false);
            
            if (input.name === "enable_rechts") {
                retchsItems.forEach(retchs => {
                    let select = retchs.querySelector('select');
                    retchs.classList.remove('disabled');
                    if (select) {
                        select.removeAttribute('disabled');
                    }
                });
            } else {
                linksItems.forEach(links => {
                    let select = links.querySelector('select');
                    links.classList.remove('disabled');
                    if (select) {
                        select.removeAttribute('disabled');
                    }
                });
            }
        } else {
            if (input.name === "enable_rechts") {
                const rechtsInput = this.querySelector('input[name="rechts_qty"]');
                if (rechtsInput) {
                    rechtsInput.value = 0;
                    const event = new Event('change', { bubbles: true });
                    rechtsInput.dispatchEvent(event);
                }
                if (!this.isPromo) {
                    retchsItems.forEach(retchs => {
                        let select = retchs.querySelector('select');
                        retchs.classList.add('disabled');
                        if (select) {
                            select.setAttribute('disabled','disabled');
                        }
                    });
                }
            } else {
                const linksInput = this.querySelector('input[name="links_qty"]');
                if (linksInput) {
                    linksInput.value = 0;
                    const event = new Event('change', { bubbles: true });
                    linksInput.dispatchEvent(event);
                }
                if (!this.isPromo) {
                    linksItems.forEach(links => {
                        let select = links.querySelector('select');
                        links.classList.add('disabled');
                        if (select) {
                            select.setAttribute('disabled','disabled');
                        }
                    });
                }
            }
        }

        this.quantityChange();

        if (window.PriceCalculator) {
            window.PriceCalculator.updateTotalPrice();
        }
    }

    ensureLensSelectsEnabled() {
        const retchsItems = document.querySelectorAll('.rechts');
        const linksItems = document.querySelectorAll('.links');
        
        [...retchsItems, ...linksItems].forEach(item => {
            let select = item.querySelector('select');
            item.classList.remove('disabled');
            if (select) {
                select.removeAttribute('disabled');
            }
        });
    }
}

// Register the custom element
customElements.define('pdp-configuration', PDPConfiguration);

//   custom price script
// Production Price Calculator
// Version with independent quantity fields for rechts and links

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      priceElement: '.price__container .price-item.price-item--regular',
      rechtsCheckbox: '#enable_rechts',
      linksCheckbox: '#enable_links',
      rechtsQtyInput: 'input[name="rechts_qty"]',
      linksQtyInput: 'input[name="links_qty"]',
      quantityButtons: '.quantity__button',
      totalPriceContainer: '.total-price',
      totalPriceStrong: '.total-price strong',
      quantityInputs: '.quantity__input'
    },
    styles: {
      fontWeight: 'bold',
      fontSize: '1.2em',
      marginTop: '10px',
      padding: '8px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px'
    }
  };
  
  let elements = {};
  let totalPriceElement = null;
  let priceObserver = null;
  
  // Initialize the price calculator
  function init() {
    try {
      findElements();
      createTotalPriceElement();
      attachEventListeners();
      setupPriceObserver();
      addQuantityButtonHandling();
      updateTotalPrice();
    } catch (error) {
      console.error('Price Calculator initialization failed:', error);
    }
  }
  
  // Find and cache DOM elements
  function findElements() {
    elements = {
      price: document.querySelector(CONFIG.selectors.priceElement),
      rechtsCheckbox: document.querySelector(CONFIG.selectors.rechtsCheckbox),
      linksCheckbox: document.querySelector(CONFIG.selectors.linksCheckbox),
      rechtsQtyInput: document.querySelector(CONFIG.selectors.rechtsQtyInput),
      linksQtyInput: document.querySelector(CONFIG.selectors.linksQtyInput),
      quantityButtons: document.querySelectorAll(CONFIG.selectors.quantityButtons),
      quantityInputs: document.querySelectorAll(CONFIG.selectors.quantityInputs)
    };
    
    // Validate required elements
    if (!elements.price) {
      console.warn('Price element not found. Calculator may not work properly.');
    }
  }
  
  // Create or find total price display element
  function createTotalPriceElement() {
    totalPriceElement = document.querySelector(CONFIG.selectors.totalPriceContainer);
    
    // The .total-price div should already exist in your Liquid template
    // We don't create it, just find it and use it
    if (!totalPriceElement) {
      console.warn('.total-price element not found. Make sure your Liquid template includes the total-price div.');
    }
  }
  
  // Extract price from text with proper decimal handling
  function extractPrice(priceText) {
    if (!priceText) return 0;
    
    // Remove currency symbols and whitespace
    let cleanPrice = priceText.replace(/[€$£\s]/g, '');
    
    // Handle European number format (comma as decimal separator)
    const commaIndex = cleanPrice.lastIndexOf(',');
    const dotIndex = cleanPrice.lastIndexOf('.');
    
    if (commaIndex > -1 && (cleanPrice.length - commaIndex) <= 3) {
      // Comma is likely decimal separator
      cleanPrice = cleanPrice.replace(/\./g, '').replace(',', '.');
    } else if (dotIndex > -1 && (cleanPrice.length - dotIndex) <= 3) {
      // Dot is likely decimal separator, remove any commas (thousands separators)
      cleanPrice = cleanPrice.replace(/,/g, '');
    }
    
    const price = parseFloat(cleanPrice);
    return isNaN(price) ? 0 : price;
  }
  
  // Add quantity button handling specifically for non-promo products
  function addQuantityButtonHandling() {
    // Check if this is a non-promo product
    const pdpConfig = document.querySelector('pdp-configuration');
    const isPromo = pdpConfig && pdpConfig.getAttribute('data-is-promo') === 'true';
    
    if (!isPromo) {
      // Find all quantity buttons that are NOT managed by Alpine.js
      const quantityButtons = document.querySelectorAll('.quantity__button:not([\\@click]):not([\\@click\\.prevent])');
      
      quantityButtons.forEach(button => {
        // Remove any existing click handlers to avoid conflicts
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        
        // Add our click handler
        newButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          
          const quantityInput = this.closest('quantity-input').querySelector('.quantity__input');
          const buttonName = this.getAttribute('name');
          const currentValue = parseInt(quantityInput.value) || 1;
          
          if (buttonName === 'plus') {
            quantityInput.value = currentValue + 1;
          } else if (buttonName === 'minus' && currentValue > 1) {
            quantityInput.value = currentValue - 1;
          }
          
          // Update button states
          updateButtonStates(quantityInput);
          
          // Trigger change event for PDPConfiguration to handle
          const event = new Event('change', { bubbles: true });
          quantityInput.dispatchEvent(event);
          
          // Update total price
          updateTotalPrice();
        });
      });
    }
  }
  
  // Update button states (enable/disable minus button)
  function updateButtonStates(input) {
    const quantityContainer = input.closest('quantity-input');
    const minusButton = quantityContainer.querySelector('button[name="minus"]');
    const currentValue = parseInt(input.value) || 1;
    
    if (minusButton) {
      if (currentValue <= 1) {
        minusButton.classList.add('disabled');
        minusButton.disabled = true;
      } else {
        minusButton.classList.remove('disabled');
        minusButton.disabled = false;
      }
    }
  }
  
  // Calculate and update total price
  function updateTotalPrice() {
    if (!elements.price || !totalPriceElement) return;
    
    try {
      // Get current base price
      const basePrice = extractPrice(elements.price.textContent || elements.price.innerText);
      
      // Get quantities (only if checkboxes are checked)
      const rechtsQty = elements.rechtsCheckbox?.checked ? 
        Math.max(0, parseInt(elements.rechtsQtyInput?.value || 0)) : 0;
      const linksQty = elements.linksCheckbox?.checked ? 
        Math.max(0, parseInt(elements.linksQtyInput?.value || 0)) : 0;
      
      // Calculate totals
      const totalQuantity = rechtsQty + linksQty;
      const totalPrice = basePrice * totalQuantity;
      
      // Log for debugging
      console.log(`Price calculation: Base: €${basePrice}, Rechts: ${rechtsQty}, Links: ${linksQty}, Total: €${totalPrice}`);
      
      // Update display
      updateTotalPriceDisplay(basePrice, totalQuantity, totalPrice);
      
    } catch (error) {
      console.error('Error updating total price:', error);
      totalPriceElement.innerHTML = '<strong>Error calculating price</strong>';
    }
  }
  
  // Update the total price display
  function updateTotalPriceDisplay(basePrice, totalQuantity, totalPrice) {
    // Find the <strong> element inside the total-price div
    const strongElement = totalPriceElement.querySelector('strong');
    
    if (strongElement) {
      // Only update the price value inside <strong>, keeping Liquid template text intact
      if (totalQuantity > 0) {
        strongElement.textContent = `€${totalPrice.toFixed(2)}`;
      } else {
        strongElement.textContent = '€0.00';
      }
    } else {
      // Fallback: if no <strong> element found, log warning
      console.warn('Strong element not found in .total-price div. Cannot update total price.');
    }
  }
  
  // Attach all event listeners
  function attachEventListeners() {
    // Checkbox change events
    if (elements.rechtsCheckbox) {
      elements.rechtsCheckbox.addEventListener('change', updateTotalPrice);
    }
    
    if (elements.linksCheckbox) {
      elements.linksCheckbox.addEventListener('change', updateTotalPrice);
    }
    
    // Quantity input events - each field can be changed independently
    if (elements.rechtsQtyInput) {
      elements.rechtsQtyInput.addEventListener('input', updateTotalPrice);
      elements.rechtsQtyInput.addEventListener('change', updateTotalPrice);
    }
    
    if (elements.linksQtyInput) {
      elements.linksQtyInput.addEventListener('input', updateTotalPrice);
      elements.linksQtyInput.addEventListener('change', updateTotalPrice);
    }
    
    // Initialize button states for all quantity inputs
    elements.quantityInputs.forEach(input => {
      updateButtonStates(input);
      
      input.addEventListener('input', function() {
        updateButtonStates(this);
      });
      
      input.addEventListener('change', function() {
        updateButtonStates(this);
      });
    });
  }
  
  // Setup price change observer for bulk discounts
  function setupPriceObserver() {
    if (!elements.price || !window.MutationObserver) return;
    
    priceObserver = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          shouldUpdate = true;
        }
      });
      
      if (shouldUpdate) {
        updateTotalPrice();
      }
    });
    
    priceObserver.observe(elements.price, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }
  
  // Cleanup function
  function destroy() {
    if (priceObserver) {
      priceObserver.disconnect();
      priceObserver = null;
    }
    
    // Remove event listeners would require keeping references
    // For production, this is typically not needed unless dynamically removing
  }
  
  // Auto-initialize when DOM is ready
  function autoInit() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      // DOM already loaded
      setTimeout(init, 100); // Small delay to ensure all elements are rendered
    }
  }
  
  // Public API (if needed)
  window.PriceCalculator = {
    init: init,
    destroy: destroy,
    updateTotalPrice: updateTotalPrice,
    version: '1.3.0'
  };
  
  // Auto-start
  autoInit();
  
})();